import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Send, Building, Heart } from 'lucide-react';
import { db } from '../firebase/config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const SponsorForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    mothersName: '',
    email: '',
    mobile: '',
    companyName: '',
    contribution: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate form
    if (!formData.name.trim()) {
      setIsSubmitting(false);
      return;
    }
    
    if (!formData.mothersName.trim()) {
      setIsSubmitting(false);
      return;
    }
    
    if (!formData.email.trim() && !formData.mobile.trim()) {
      setIsSubmitting(false);
      return;
    }
    
    if (!formData.contribution.trim()) {
      setIsSubmitting(false);
      return;
    }

    try {
      // Save sponsor data to Firestore
      const sponsorData = {
        name: formData.name.trim(),
        mothersName: formData.mothersName.trim(),
        email: formData.email.trim() || '',
        mobile: formData.mobile.trim() || '',
        companyName: formData.companyName.trim() || '',
        contribution: formData.contribution.trim(),
        status: 'pending', // pending, contacted, confirmed, declined
        submittedAt: serverTimestamp(),
        contactedAt: null,
        contactedBy: null,
        notes: ''
      };

      await addDoc(collection(db, 'sponsors'), sponsorData);
      
      // Sponsor inquiry submitted successfully
      
      setSubmitSuccess(true);
      setTimeout(() => {
        onClose();
        setSubmitSuccess(false);
        setFormData({
          name: '',
          mothersName: '',
          email: '',
          mobile: '',
          companyName: '',
          contribution: ''
        });
      }, 2000);
    } catch (error) {
      // Error submitting sponsor inquiry
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Become Our Sponsor
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X size={24} />
          </button>
        </div>

        {submitSuccess ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="text-green-600 dark:text-green-400" size={24} />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Thank You for Your Interest!
            </h4>
            <p className="text-gray-600 dark:text-gray-400">
              We'll contact you soon to discuss sponsorship opportunities.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Heart className="text-primary-600 dark:text-primary-400" size={20} />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Help us build an educated generation with a growth mindset. 
                Your sponsorship empowers students to achieve their dreams.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Enter your full name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mother's Name *
              </label>
              <input
                type="text"
                name="mothersName"
                value={formData.mothersName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Enter your mother's name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Enter your email address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mobile Number
              </label>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Enter your mobile number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Company Name (if any)
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Enter your company name"
                />
                <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                How would you like to contribute? *
              </label>
              <textarea
                name="contribution"
                value={formData.contribution}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                placeholder="Describe how you would like to support our educational mission..."
                required
              />
            </div>

            <div className="text-xs text-gray-500 dark:text-gray-400">
              * At least one contact method (email or mobile) is required
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary py-3 flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Heart className="w-4 h-4 mr-2" />
                  Submit Sponsorship Inquiry
                </>
              )}
            </button>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
};

export default SponsorForm;
